import React, { useState } from 'react';
import { X } from 'lucide-react';

// ===== FOOTER AVEC LIENS LÉGAUX =====
const Footer = ({ onShowLegal }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-800 text-white mt-12 py-8">
      <div className="container mx-auto px-4">
        {/* Disclaimer principal */}
        <div className="mb-6 p-4 bg-gray-700 rounded-lg text-sm">
          <p className="text-center">
            ⚽ <strong>Disclaimer:</strong> Ce site est un projet de fan créé par des fans pour des fans. 
            Il n'est <strong>pas affilié, approuvé ou sponsorisé</strong> par Level-5 Inc. ou toute autre entité 
            liée à la franchise Inazuma Eleven. Tous les droits appartiennent à leurs propriétaires respectifs.
          </p>
        </div>

        {/* Liens légaux */}
        <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
          <button
            onClick={() => onShowLegal('mentions')}
            className="text-gray-300 hover:text-white underline"
          >
            Mentions Légales
          </button>
          <span className="text-gray-500">•</span>
          <button
            onClick={() => onShowLegal('privacy')}
            className="text-gray-300 hover:text-white underline"
          >
            Politique de Confidentialité
          </button>
          <span className="text-gray-500">•</span>
          <button
            onClick={() => onShowLegal('cookies')}
            className="text-gray-300 hover:text-white underline"
          >
            Cookies
          </button>
          <span className="text-gray-500">•</span>
          <button
            onClick={() => onShowLegal('disclaimer')}
            className="text-gray-300 hover:text-white underline"
          >
            Avertissement
          </button>
        </div>

        {/* Copyright */}
        <div className="text-center text-sm text-gray-400">
          <p>
            © {currentYear} Inazuma Eleven Lab - Projet de fan non-officiel et non-lucratif
          </p>
          <p className="mt-2 text-xs">
            Inazuma Eleven™ est une marque déposée de Level-5 Inc.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ===== BANNIÈRE COOKIES RGPD =====
const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(() => {
    return !localStorage.getItem('cookies-accepted');
  });

  const acceptCookies = () => {
    localStorage.setItem('cookies-accepted', 'true');
    setIsVisible(false);
  };

  const refuseCookies = () => {
    localStorage.setItem('cookies-accepted', 'false');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50 border-t-2 border-indigo-500">
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm">
            🍪 Ce site utilise uniquement le stockage local (localStorage) pour sauvegarder vos équipes 
            et préférences sur votre appareil. <strong>Aucune donnée personnelle n'est collectée ou envoyée à des serveurs tiers.</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={acceptCookies}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold text-sm whitespace-nowrap"
          >
            J'accepte
          </button>
          <button
            onClick={refuseCookies}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold text-sm whitespace-nowrap"
          >
            Refuser
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== MODAL PAGES LÉGALES =====
const LegalModal = ({ type, onClose }) => {
  if (!type) return null;

  const content = {
    mentions: {
      title: "Mentions Légales",
      body: (
        <>
          <h2 className="text-xl font-bold mb-4">Éditeur du site</h2>
          <p className="mb-4">
            Ce site est un projet personnel à but non lucratif créé par un fan de la série Inazuma Eleven.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Hébergement</h2>
          <p className="mb-4">
            Ce site est hébergé sur Netlify :<br />
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Propriété intellectuelle</h2>
          <p className="mb-4">
            <strong>Inazuma Eleven™</strong> est une marque déposée de <strong>Level-5 Inc.</strong>
          </p>
          <p className="mb-4">
            Tous les personnages, noms, images et autres contenus liés à Inazuma Eleven sont la propriété 
            de Level-5 Inc. et sont utilisés ici dans le cadre de l'usage équitable (fair use) à des fins 
            informatives et éducatives uniquement.
          </p>
          <p className="mb-4">
            Ce site n'est <strong>pas affilié, approuvé, sponsorisé ou autorisé</strong> par Level-5 Inc. 
            ou toute autre entité liée à la franchise Inazuma Eleven.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">But du site</h2>
          <p className="mb-4">
            Ce site est un outil gratuit créé par des fans pour des fans, permettant d'optimiser 
            et de gérer des équipes dans les jeux Inazuma Eleven. Il ne génère <strong>aucun revenu</strong> et
            n'a <strong>aucun but commercial</strong>.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Contact</h2>
          <p className="mb-4">
            Pour toute question ou demande de retrait de contenu :<br />
            astraprodtv@gmail.com
          </p>
        </>
      )
    },

    privacy: {
      title: "Politique de Confidentialité",
      body: (
        <>
          <p className="mb-4 text-sm text-gray-600">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>

          <h2 className="text-xl font-bold mb-4">Collecte de données</h2>
          <p className="mb-4">
            <strong>Ce site ne collecte AUCUNE donnée personnelle.</strong>
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Pas de compte utilisateur</li>
            <li>Pas d'adresse email demandée</li>
            <li>Pas de cookies de tracking</li>
            <li>Pas de connexion à des serveurs externes</li>
            <li>Pas d'analytics ou de suivi comportemental</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Stockage local</h2>
          <p className="mb-4">
            Le site utilise uniquement le <strong>localStorage</strong> de votre navigateur pour :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Sauvegarder vos équipes créées</li>
            <li>Conserver vos préférences d'affichage</li>
            <li>Mémoriser votre acceptation des cookies</li>
          </ul>
          <p className="mb-4">
            Ces données sont stockées <strong>uniquement sur votre appareil</strong> et ne sont 
            <strong> jamais envoyées</strong> à des serveurs tiers.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Conformité RGPD</h2>
          <p className="mb-4">
            Ce site est conforme au Règlement Général sur la Protection des Données (RGPD) car :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Aucune donnée personnelle n'est collectée</li>
            <li>Aucun traitement de données à caractère personnel</li>
            <li>Aucun transfert de données vers des pays tiers</li>
            <li>Vous pouvez supprimer vos données à tout moment en vidant le cache de votre navigateur</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Vos droits</h2>
          <p className="mb-4">
            Bien qu'aucune donnée ne soit collectée, vous avez le droit de :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Supprimer vos données :</strong> Vider le cache/localStorage de votre navigateur</li>
            <li><strong>Refuser les cookies :</strong> Via la bannière de consentement</li>
            <li><strong>Nous contacter :</strong> Pour toute question sur la confidentialité</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Services tiers</h2>
          <p className="mb-4">
            Ce site <strong>n'utilise aucun service tiers</strong> de tracking, analytics ou publicité.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Modifications</h2>
          <p className="mb-4">
            Cette politique peut être mise à jour occasionnellement. La date de dernière modification 
            est indiquée en haut de cette page.
          </p>
        </>
      )
    },

    cookies: {
      title: "Politique de Cookies",
      body: (
        <>
          <h2 className="text-xl font-bold mb-4">Qu'est-ce qu'un cookie ?</h2>
          <p className="mb-4">
            Un cookie est un petit fichier texte stocké sur votre appareil lorsque vous visitez un site web.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Cookies utilisés par ce site</h2>
          <p className="mb-4">
            Ce site <strong>n'utilise PAS de cookies traditionnels</strong>. À la place, nous utilisons 
            le <strong>localStorage</strong>, qui fonctionne de manière similaire mais reste uniquement 
            sur votre appareil.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">localStorage utilisé</h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Nom</th>
                  <th className="text-left py-2">But</th>
                  <th className="text-left py-2">Durée</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2"><code>cookies-accepted</code></td>
                  <td className="py-2">Mémorise votre choix concernant les cookies</td>
                  <td className="py-2">Permanent</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2"><code>saved-teams</code></td>
                  <td className="py-2">Sauvegarde vos équipes créées</td>
                  <td className="py-2">Permanent</td>
                </tr>
                <tr>
                  <td className="py-2"><code>user-preferences</code></td>
                  <td className="py-2">Sauvegarde vos préférences d'affichage</td>
                  <td className="py-2">Permanent</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-bold mb-4 mt-6">Cookies tiers</h2>
          <p className="mb-4">
            <strong>Ce site n'utilise AUCUN cookie tiers.</strong>
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Pas de Google Analytics</li>
            <li>Pas de Facebook Pixel</li>
            <li>Pas de publicité</li>
            <li>Pas de tracking comportemental</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Gérer vos préférences</h2>
          <p className="mb-4">
            Vous pouvez à tout moment :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Supprimer le localStorage via les paramètres de votre navigateur</li>
            <li>Utiliser le mode navigation privée pour éviter tout stockage</li>
            <li>Désactiver JavaScript (le site ne fonctionnera alors pas)</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Comment supprimer les données ?</h2>
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Chrome / Edge :</p>
            <p className="text-sm">Paramètres → Confidentialité et sécurité → Effacer les données de navigation → localStorage</p>
            
            <p className="font-semibold mb-2 mt-4">Firefox :</p>
            <p className="text-sm">Options → Vie privée et sécurité → Cookies et données de sites → Gérer les données</p>
            
            <p className="font-semibold mb-2 mt-4">Safari :</p>
            <p className="text-sm">Préférences → Confidentialité → Gérer les données de sites web</p>
          </div>
        </>
      )
    },

    disclaimer: {
      title: "Avertissement et Clause de non-responsabilité",
      body: (
        <>
          <h2 className="text-xl font-bold mb-4">Projet de fan non-officiel</h2>
          <p className="mb-4">
            Ce site est un <strong>projet de fan créé par des fans pour des fans</strong> de la série 
            Inazuma Eleven. Il s'agit d'un projet personnel, gratuit et sans but lucratif.
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Non-affiliation avec Level-5</h2>
          <p className="mb-4 bg-yellow-50 border-l-4 border-yellow-500 p-4">
            <strong>⚠️ IMPORTANT :</strong> Ce site n'est <strong>PAS affilié, approuvé, sponsorisé ou autorisé</strong> 
            par Level-5 Inc., ni par aucune entité officielle liée à la franchise Inazuma Eleven.
          </p>
          <p className="mb-4">
            Tous les droits sur Inazuma Eleven, ses personnages, son univers et ses marques appartiennent 
            exclusivement à <strong>Level-5 Inc.</strong>
          </p>

          <h2 className="text-xl font-bold mb-4 mt-6">Usage équitable (Fair Use)</h2>
          <p className="mb-4">
            L'utilisation de contenus liés à Inazuma Eleven (noms de personnages, statistiques de jeu, images) 
            sur ce site est réalisée dans le cadre de l'<strong>usage équitable (fair use)</strong> à des fins :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Informatives</li>
            <li>Éducatives</li>
            <li>De critique et de commentaire</li>
            <li>Non commerciales</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Absence de but lucratif</h2>
          <p className="mb-4">
            Ce site est <strong>entièrement gratuit</strong> et ne génère <strong>aucun revenu</strong> :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Aucune publicité</li>
            <li>Aucun abonnement payant</li>
            <li>Aucune vente de produits ou services</li>
            <li>Aucune monétisation d'aucune sorte</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Exactitude des informations</h2>
          <p className="mb-4">
            Bien que nous nous efforcions de fournir des informations exactes et à jour concernant 
            les jeux Inazuma Eleven, nous ne pouvons garantir :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>L'exactitude à 100% des statistiques et données</li>
            <li>La mise à jour constante en cas de modifications dans les jeux</li>
            <li>L'absence d'erreurs ou d'omissions</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Limitation de responsabilité</h2>
          <p className="mb-4">
            L'utilisation de ce site est à vos propres risques. Nous ne sommes pas responsables :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Des dommages directs ou indirects liés à l'utilisation du site</li>
            <li>De la perte de données stockées localement</li>
            <li>Des erreurs dans les calculs ou statistiques</li>
            <li>De l'interruption ou de l'indisponibilité du service</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Respect des droits d'auteur</h2>
          <p className="mb-4">
            Si vous êtes un représentant de Level-5 Inc. ou si vous estimez que ce site porte atteinte 
            à vos droits, veuillez nous contacter immédiatement. Nous nous engageons à :
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Répondre rapidement à toute demande légitime</li>
            <li>Retirer tout contenu problématique dans les plus brefs délais</li>
            <li>Coopérer pleinement pour résoudre tout problème</li>
          </ul>

          <h2 className="text-xl font-bold mb-4 mt-6">Contact</h2>
          <p className="mb-4">
            Pour toute question, demande de retrait ou autre préoccupation :<br />
            <strong>astraprodtv@gmail.com</strong>
          </p>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-6">
            <p className="font-semibold">💚 Merci Level-5 !</p>
            <p className="text-sm mt-2">
              Ce projet existe grâce à l'univers incroyable créé par Level-5. Nous espérons qu'il 
              contribue à maintenir la passion de la communauté des fans d'Inazuma Eleven vivante.
            </p>
          </div>
        </>
      )
    }
  };

  const currentContent = content[type];
  if (!currentContent) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-800">{currentContent.title}</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 text-gray-700">
          {currentContent.body}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export { Footer, CookieBanner, LegalModal };
